<template>
  <component
    :is="tag"
    :id="id"
    :href="href ? href : undefined"
    :to="modLink || to"
    :target="target"
    class="action-box"
    :class="[
      {
        'action-box--centered': centered,
        'action-box--clickable': clickable,
        'action-box--rebrand': rebrand,
        'action-box--borderless': borderless,
        'action-box--border-bold': borderBold,
        'action-box--disabled': disabled,
      },
      borderColor ? 'action-box--border-color-' + borderColor : '',
    ]"
    role="button"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space="handleKeydownSpace"
  >
    <div
      v-if="$slots.before"
      class="ab-before"
    >
      <slot name="before" />
    </div>

    <div class="ab-main">
      <slot name="default" />
    </div>

    <div
      v-if="$slots.after"
      class="ab-after"
    >
      <slot name="after" />
    </div>
  </component>
</template>

<script>
import { NuxtLink } from '#components';
import ModLink from '@/mixins/ModLink';

export default {
  mixins: [ModLink],
  props: {
    borderColor: {
      type: String,
      default: '',
    },
    borderBold: {
      type: Boolean,
    },
    centered: {
      type: Boolean,
    },
    clickable: {
      type: Boolean,
      default: true,
    },
    href: {
      type: String,
      default: undefined,
    },
    inlineLinks: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: null,
    },
    to: {
      type: [Object, String],
      default: null,
    },
    target: {
      type: String,
      default: '',
    },
    rebrand: {
      type: Boolean,
    },
    borderless: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  },
  emits: ['click'],
  computed: {
    tag() {
      if (this.$router && this.to) {
        return NuxtLink;
      }

      if (this.href) {
        return 'a';
      }

      return 'div';
    },
    link() {
      return this.to || this.href || this.inlineLinks;
    },
  },
  methods: {
    handleClick(event) {
      if (this.link || event.target.classList.contains('link-text')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.$emit('click', event);
    },
    handleKeydownSpace(event) {
      if (this.link) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        return navigateTo(this.link, {
          external: this.href ? this.href : undefined,
          target: this.target,
        });
      }

      if (event.target.tagName === 'INPUT') {
        return;
      }

      this.$emit('click', event);
    },
  },
};
</script>

<style lang="scss" scoped>
.action-box {
  position: relative;
  display: flex;
  padding: $pad;
  background-color: $color-general-background-neutral-default;
  border: 1px solid palette(grey, light);
  border-radius: map-get($border-radiuses, 'l');
  text-decoration: none;
  color: palette(black);

  &.action-box--border-bold {
    border-width: 3px;
  }

  &.action-box--border-color-grey {
    border-color: palette(grey);
  }

  &.action-box--border-color-orientation-green {
    border-color: palette(orientation, green);
  }

  .ab-before {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-right: $pad;
  }

  .ab-main {
    flex: 1 0;
    min-width: 0;
    text-align: left;
    align-self: center;
  }

  .ab-after {
    margin-left: $pad;
  }

  &.action-box--centered {
    .ab-before,
    .ab-after {
      display: flex;
      align-items: center;
    }
  }

  &.action-box--clickable {
    cursor: pointer;
  }

  &.action-box--rebrand {
    border-radius: map-get($border-radiuses, 'xxl');
    margin: 0 0 $pad;
  }

  &.action-box--borderless {
    border: none;
    background: palette(grey, lighter);
  }

  &.action-box--disabled {
    background-color: $color-general-background-neutral-default;

    &:hover {
      background-color: $color-general-background-neutral-default;
    }
  }
}
</style>
